import BorderBottomIcon from "@mui/icons-material/BorderBottom";
import BorderClearIcon from "@mui/icons-material/BorderClear";
import BorderLeftIcon from "@mui/icons-material/BorderLeft";
import BorderOuterIcon from "@mui/icons-material/BorderOuter";
import BorderRightIcon from "@mui/icons-material/BorderRight";
import BorderTopIcon from "@mui/icons-material/BorderTop";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  BorderSide,
  DEFAULT_CELL_BORDER_COLOR
} from "~/features/andon/andon.types";

import TempZoneColorPresets from "./TempZoneColorPresets";

export type CellBorderControlsProps = {
  cellBorderSides: BorderSide[];
  cellBorderColor: string;
  onSetCellBorder: (sides: BorderSide[], color: string) => void;
};

export default function CellBorderControls({
  cellBorderSides,
  cellBorderColor,
  onSetCellBorder
}: CellBorderControlsProps) {
  const { t } = useTranslation();
  const [borderColor, setBorderColor] = useState(
    cellBorderColor || DEFAULT_CELL_BORDER_COLOR
  );

  useEffect(() => {
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-derived-state
    setBorderColor(cellBorderColor || DEFAULT_CELL_BORDER_COLOR);
  }, [cellBorderColor]);

  const handleColorChange = (color: string) => {
    setBorderColor(color);
    if (cellBorderSides.length > 0) {
      onSetCellBorder(cellBorderSides, color);
    }
  };

  return (
    <Box sx={{ px: 2, pb: 1 }}>
      <Typography
        variant="overline"
        color="text.secondary"
        sx={{ display: "block" }}
      >
        {t("borders")}
      </Typography>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <ToggleButtonGroup
          size="small"
          value={cellBorderSides}
          onChange={(_e, next) =>
            onSetCellBorder(next as BorderSide[], borderColor)
          }
          aria-label={t("borders")}
        >
          <ToggleButton value="top" aria-label={t("border top")}>
            <BorderTopIcon fontSize="small" />
          </ToggleButton>
          <ToggleButton value="left" aria-label={t("border left")}>
            <BorderLeftIcon fontSize="small" />
          </ToggleButton>
          <ToggleButton value="right" aria-label={t("border right")}>
            <BorderRightIcon fontSize="small" />
          </ToggleButton>
          <ToggleButton value="bottom" aria-label={t("border bottom")}>
            <BorderBottomIcon fontSize="small" />
          </ToggleButton>
        </ToggleButtonGroup>
        <Tooltip title={t("border color")}>
          <Box
            component="input"
            type="color"
            aria-label={t("border color")}
            value={borderColor}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              handleColorChange(e.target.value)
            }
            sx={{
              width: 32,
              height: 32,
              p: 0,
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1,
              cursor: "pointer",
              flexShrink: 0
            }}
          />
        </Tooltip>
        <Tooltip title={t("outline")}>
          <IconButton
            size="small"
            aria-label={t("outline")}
            onClick={() =>
              onSetCellBorder(["top", "right", "bottom", "left"], borderColor)
            }
          >
            <BorderOuterIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title={t("clear borders")}>
          <IconButton
            size="small"
            aria-label={t("clear borders")}
            onClick={() => onSetCellBorder([], borderColor)}
          >
            <BorderClearIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
      <Box sx={{ mt: 1 }}>
        <TempZoneColorPresets onSelect={handleColorChange} />
      </Box>
    </Box>
  );
}
